import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, Radio, Clock, Trophy, Dices, Send, Instagram } from 'lucide-react';
import CricketAPIService from '../services/CricketAPIService';

const PATH_MAP = {
  'Cricket': '/cricket',
  'Casino': '/casino',
};

// Sidebar blocklist — hide minor/domestic/virtual leagues from the Cricket tree.
// A whitelist of major tournaments takes precedence so IPL/PSL/internationals are
// never dropped even if their names happen to contain a blocked substring.
const SIDEBAR_ALLOWED = [
  'indian premier league',
  'pakistan super league',
  'international',
  'world cup',
  'champions trophy',
  'asia cup',
  'womens',
];
const SIDEBAR_BLOCKED = [
  'navi mumbai',
  'prime minister cup',
  'premier league', // domestic only (IPL/PSL pass via SIDEBAR_ALLOWED)
  'county',
  'vitality',
  'hundred',
  'sheffield',
  'super smash',
  'caribbean premier',
  'list-a',
  'inter-provincial',
  'ram slam',
  'global t20',
  'euro t20',
  'mzansi',
  'afghanistan premier',
  'bangladesh premier',
  'lanka premier',
  'srl',
  'virtual',
  't20 brisbane champions',
  'brisbane champions',
  'u19 t20',
  'australia tri-series',
  'one-day cup, women',
  'one-day cup women',
  // Kept in step with config/blockedLeagues.js. The feed spells it 'Inter Provincial Cup'
  // with no hyphen, so the 'inter-provincial' term above never matches — this one does.
  'major league cricket',
  'inter provincial',
  'pondicherry',
  'u19', // age-group fixtures; the feed writes "U19" unhyphenated
];
const isSidebarBlocked = (name) => {
  if (!name) return false;
  const lower = name.toLowerCase();
  if (SIDEBAR_ALLOWED.some((a) => lower.includes(a))) return false;
  return SIDEBAR_BLOCKED.some((b) => lower.includes(b));
};

const SPORTS_LIST = [
  { id: 1, name: 'Cricket', Icon: Trophy },
  { id: 4, name: 'Casino', Icon: Dices },
];

const QUICK_NAV = [
  { name: 'IN PLAY', Icon: Radio, path: '/', pulse: true },
  { name: 'UPCOMING', Icon: Clock, path: '/' },
];

const Sidebar = ({ activeSport, setActiveSport, onNavigate }) => {
  const navigate = useNavigate();
  const [expandedSport, setExpandedSport] = useState(null);
  const [expandedComp, setExpandedComp] = useState(null);
  const [cricketTree, setCricketTree] = useState(null); // { compName: [matches] }
  const [loading, setLoading] = useState(false);

  // Read contact links from localStorage (updated by Header.fetchUserBalance)
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('reddyannabook_user') || '{}'); }
    catch { return {}; }
  })();
  const contactLinks = {
    telegram: storedUser.telegram_link || 'https://t.me/reddyannabookofficial',
    instagram: storedUser.instagram_link || 'https://instagram.com/reddyannabookofficial',
  };

  // Fetch real cricket data when Cricket is expanded
  useEffect(() => {
    if (expandedSport !== 'Cricket' || cricketTree) return;
    setLoading(true);
    (async () => {
      try {
        const data = await CricketAPIService.getAllMatches();
        const matches = data?.matches || [];
        // Group by competition name (matches are already parsed by CricketAPIService)
        const tree = {};
        for (const m of matches) {
          const comp = m.competition || m.league || 'Others';
          // Hide minor/domestic/virtual leagues from sidebar
          if (isSidebarBlocked(comp)) continue;
          const matchName = m.name || `${m.home_team} v ${m.away_team}` || '?';
          const matchId = m.groupById || m.match_id || m.id;
          const inPlay = m.inPlay || false;
          if (!tree[comp]) tree[comp] = [];
          if (!tree[comp].some(x => x.id === matchId)) {
            tree[comp].push({ id: matchId, name: matchName, inPlay });
          }
        }
        // Sort: IPL first, then PSL, then alphabetical
        const priority = ['Indian Premier League', 'Pakistan Super League'];
        const sorted = {};
        for (const p of priority) { if (tree[p]) sorted[p] = tree[p]; }
        for (const k of Object.keys(tree).sort()) { if (!sorted[k]) sorted[k] = tree[k]; }
        setCricketTree(sorted);
      } catch { setCricketTree({}); }
      setLoading(false);
    })();
  }, [expandedSport, cricketTree]);

  const handleSportClick = (name) => {
    if (name === 'Cricket') {
      setExpandedSport(prev => prev === 'Cricket' ? null : 'Cricket');
      setExpandedComp(null);
      setActiveSport('Cricket');
    } else {
      setActiveSport(name);
      if (onNavigate) onNavigate();
      navigate(PATH_MAP[name] || '/');
    }
  };

  const handleMatchClick = (matchId) => {
    setActiveSport('Cricket');
    if (onNavigate) onNavigate();
    navigate(`/match/${matchId}`);
  };

  return (
    <div
      className="w-64 border-r border-gray-200 overflow-y-auto h-full"
      style={{
        background: 'linear-gradient(180deg, #f8f8f8 0%, #fafaf5 100%)',
        borderTop: '3px solid #fbbf24',
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* MENU label */}
      <div
        className="uppercase"
        style={{ fontSize: 11, color: '#6b7280', letterSpacing: 2, fontWeight: 700, padding: '14px 16px 8px' }}
      >
        Menu
      </div>

      {/* Deposit/Withdraw */}
      <div className="p-3 border-b border-gray-200">
        <div className="hidden md:grid grid-cols-2 gap-2">
          <button onClick={() => navigate('/deposit')} className="py-2 rounded-lg bg-kabook-orange hover:bg-kabook-orange-dark text-white font-semibold text-sm transition-colors">Deposit</button>
          <button onClick={() => navigate('/withdraw')} className="py-2 rounded-lg bg-kabook-black hover:bg-gray-800 text-white font-semibold text-sm transition-colors">Withdraw</button>
        </div>
      </div>

      {/* Quick Nav (IN PLAY / UPCOMING / FAVOURITES) */}
      <ul className="list-none p-0 m-0 pt-2 border-b border-gray-200">
        {QUICK_NAV.map((q) => {
          const Icon = q.Icon;
          return (
            <li key={q.name}>
              <button
                type="button"
                onClick={() => { if (onNavigate) onNavigate(); navigate(q.path); }}
                className="kabook-nav-item w-full flex items-center gap-3 text-left hover:bg-[#fef3c7] border-l-[3px] border-transparent"
                style={{ padding: '8px 16px' }}
              >
                <span
                  className="relative flex items-center justify-center flex-shrink-0 kabook-nav-icon"
                  style={{ width: 28, height: 28, borderRadius: 9999, background: '#fef3c7', color: '#f59e0b' }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {q.pulse && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 ring-2 ring-white animate-pulse" />
                  )}
                </span>
                <span className="text-[13px] font-semibold uppercase tracking-wide text-kabook-text-dark truncate">
                  {q.name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Sports Menu */}
      <ul className="py-2 list-none p-0 m-0">
        {SPORTS_LIST.map((sport) => {
          const isCricket = sport.name === 'Cricket';
          const isExpanded = expandedSport === sport.name;
          const isActive = activeSport === sport.name;
          const Icon = sport.Icon;

          return (
            <li key={sport.id}>
              <button type="button" onClick={() => handleSportClick(sport.name)}
                className={`kabook-nav-item w-full flex items-center gap-3 text-left hover:bg-[#fef3c7] border-l-[3px] ${isActive ? 'bg-[#fef3c7] border-kabook-orange' : 'border-transparent'}`}
                style={{ padding: '8px 16px' }}>
                {isCricket ? (
                  <span className="flex-shrink-0 w-3 flex items-center justify-center text-gray-500">
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </span>
                ) : <span className="w-3 flex-shrink-0" />}
                <span
                  className="flex items-center justify-center flex-shrink-0 kabook-nav-icon"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 9999,
                    background: isActive ? '#f59e0b' : '#fef3c7',
                    color: isActive ? '#ffffff' : '#f59e0b',
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className={`flex-1 truncate ${isActive && !isCricket ? 'text-kabook-orange-hover font-semibold' : 'text-kabook-text-dark font-medium'}`} style={{ fontSize: 13 }}>
                  {sport.name}
                </span>
              </button>

              {/* Cricket competitions tree — from real API data */}
              {isCricket && isExpanded && (
                <ul className="list-none pl-0 pr-0 border-l border-gray-100 ml-4">
                  {loading ? (
                    <li className="flex items-center gap-2 pl-6 py-3 text-xs text-gray-400">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                    </li>
                  ) : cricketTree && Object.keys(cricketTree).length > 0 ? (
                    Object.entries(cricketTree).map(([comp, matches]) => {
                      const isCompExpanded = expandedComp === comp;
                      return (
                        <li key={comp}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setExpandedComp(prev => prev === comp ? null : comp); }}
                            className="w-full flex items-center gap-2 pl-6 pr-4 py-2 text-left hover:bg-gray-50 transition-colors text-gray-700">
                            <span className="flex-shrink-0 w-4 flex items-center justify-center text-gray-400">
                              {isCompExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </span>
                            <span className="text-xs font-medium truncate flex-1">{comp}</span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">{matches.length}</span>
                          </button>
                          {isCompExpanded && (
                            <ul className="list-none pl-0 pr-0 bg-gray-50/50 border-l border-gray-100 ml-4">
                              {matches.map((match) => (
                                <li key={match.id}>
                                  <button type="button" onClick={() => handleMatchClick(match.id)}
                                    className="w-full text-left pl-8 pr-4 py-2 text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900 truncate">
                                    {match.inPlay && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse" />}
                                    {match.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })
                  ) : (
                    <li className="pl-6 py-3 text-xs text-gray-400">No matches available</li>
                  )}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {/* Telegram + Instagram — below sports list */}
      <div className="px-3 pb-3 pt-3" style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid #e5e5e5' }}>
        <div
          className="uppercase"
          style={{ fontSize: 10, color: '#6b7280', letterSpacing: 2, fontWeight: 700, padding: '0 4px 4px' }}
        >
          Connect
        </div>
        <a href={contactLinks.telegram} target="_blank" rel="noopener noreferrer"
          className="kabook-social-btn flex items-center justify-center gap-2 w-full text-white font-semibold"
          style={{
            height: 44,
            padding: '0 16px',
            borderRadius: 10,
            fontSize: 13,
            background: 'linear-gradient(135deg, #0088cc 0%, #006699 100%)',
            boxShadow: '0 3px 8px rgba(0,136,204,0.3)',
          }}>
          <Send className="w-4 h-4" />
          <span>Join Us On Telegram</span>
        </a>
        <a href={contactLinks.instagram} target="_blank" rel="noopener noreferrer"
          className="kabook-social-btn flex items-center justify-center gap-2 w-full text-white font-semibold"
          style={{
            height: 44,
            padding: '0 16px',
            borderRadius: 10,
            fontSize: 13,
            background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
            boxShadow: '0 3px 8px rgba(225,48,108,0.3)',
          }}>
          <Instagram className="w-4 h-4" />
          <span>Follow Us On Instagram</span>
        </a>
      </div>
    </div>
  );
};

export default Sidebar;
